import { supabase, isSupabaseConfigured } from '../config/supabase';

// Get Supabase URL for fallback URL construction
const getSupabaseUrl = () => {
  return process.env.REACT_APP_SUPABASE_URL || 'https://wihhgnrnybuiorvnztoq.supabase.co';
};

/**
 * Upload images to Supabase Storage
 * @param {File[]} files - Array of image files
 * @returns {Promise<string[]>} Array of public URLs
 */
export const uploadMenuImages = async (files) => {
  // Check if Supabase is configured
  if (!isSupabaseConfigured() || !supabase || !supabase.storage) {
    console.warn('Supabase Storage not configured. Using local file URLs for demo.');
    
    // Return local object URLs for demo purposes
    const demoUrls = [];
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error(`${file.name} is not an image file`);
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(`${file.name} is too large. Maximum size is 5MB`);
      }

      // Create local object URL (for demo - will be lost on page refresh)
      const localUrl = URL.createObjectURL(file);
      demoUrls.push(localUrl);
    }
    
    return demoUrls;
  }

  const uploadedUrls = [];
  
  try {
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error(`${file.name} is not an image file`);
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(`${file.name} is too large. Maximum size is 5MB`);
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `menu-items/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('menu-images') // Your bucket name
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });

      if (error) {
        console.error('Error uploading image:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('menu-images')
        .getPublicUrl(filePath);

      if (urlData?.publicUrl) {
        uploadedUrls.push(urlData.publicUrl);
      } else {
        // Fallback: construct URL manually
        const projectUrl = getSupabaseUrl().replace('/rest/v1', '');
        const publicUrl = `${projectUrl}/storage/v1/object/public/menu-images/${filePath}`;
        uploadedUrls.push(publicUrl);
      }
    }

    return uploadedUrls;
  } catch (error) {
    console.error('Error in uploadMenuImages:', error);
    throw error;
  }
};

/**
 * Delete image from Supabase Storage
 * @param {string} imageUrl - Full URL of the image to delete
 */
export const deleteMenuImage = async (imageUrl) => {
  // Check if Supabase is configured
  if (!isSupabaseConfigured() || !supabase || !supabase.storage) {
    // For demo mode, just revoke the local object URL
    if (imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
    }
    return { success: true };
  }

  try {
    // Extract file path from URL
    // URL format: https://[project].supabase.co/storage/v1/object/public/menu-images/menu-items/filename.jpg
    const urlParts = imageUrl.split('/');
    const bucketIndex = urlParts.findIndex(part => part === 'menu-images');
    
    if (bucketIndex === -1) {
      // If it's a local blob URL, just revoke it
      if (imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
        return { success: true };
      }
      throw new Error('Invalid image URL format');
    }
    
    // Get the path after 'menu-images'
    const filePath = urlParts.slice(bucketIndex + 1).join('/');
    
    const { error } = await supabase.storage
      .from('menu-images')
      .remove([filePath]);

    if (error) {
      console.error('Error deleting image:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error in deleteMenuImage:', error);
    throw error;
  }
};

/**
 * Delete multiple images from Supabase Storage
 * @param {string[]} imageUrls - Array of image URLs to delete
 */
export const deleteMenuImages = async (imageUrls) => {
  // Check if Supabase is configured
  if (!isSupabaseConfigured() || !supabase || !supabase.storage) {
    // For demo mode, revoke all local object URLs
    imageUrls.forEach(url => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    return { success: true };
  }

  try {
    const filePaths = imageUrls.map(url => {
      // Handle local blob URLs
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
        return null;
      }
      
      const urlParts = url.split('/');
      const bucketIndex = urlParts.findIndex(part => part === 'menu-images');
      if (bucketIndex === -1) return null;
      return urlParts.slice(bucketIndex + 1).join('/');
    }).filter(path => path !== null);

    if (filePaths.length === 0) {
      return { success: true };
    }

    const { error } = await supabase.storage
      .from('menu-images')
      .remove(filePaths);

    if (error) {
      console.error('Error deleting images:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error in deleteMenuImages:', error);
    throw error;
  }
};

/**
 * Upload restaurant logo to Supabase Storage
 * @param {File} file - Logo image file
 * @returns {Promise<string>} Public URL of the uploaded logo
 */
export const uploadRestaurantLogo = async (file) => {
  // Check if Supabase is configured
  if (!isSupabaseConfigured() || !supabase || !supabase.storage) {
    console.warn('Supabase Storage not configured. Using local file URL for demo.');
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size (2MB max for logos)
    if (file.size > 2 * 1024 * 1024) {
      throw new Error('Logo file is too large. Maximum size is 2MB');
    }

    // Create local object URL (for demo - will be lost on page refresh)
    return URL.createObjectURL(file);
  }

  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size (2MB max for logos)
    if (file.size > 2 * 1024 * 1024) {
      throw new Error('Logo file is too large. Maximum size is 2MB');
    }

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `logo-${Date.now()}.${fileExt}`;
    const filePath = fileName; // Store directly in bucket root

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('restaurant-logos') // Your bucket name
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true, // Allow overwriting existing logo
        contentType: file.type
      });

    if (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('restaurant-logos')
      .getPublicUrl(filePath);

    if (urlData?.publicUrl) {
      return urlData.publicUrl;
    } else {
      // Fallback: construct URL manually
      const projectUrl = getSupabaseUrl().replace('/rest/v1', '');
      return `${projectUrl}/storage/v1/object/public/restaurant-logos/${filePath}`;
    }
  } catch (error) {
    console.error('Error in uploadRestaurantLogo:', error);
    throw error;
  }
};

/**
 * Delete restaurant logo from Supabase Storage
 * @param {string} logoUrl - Full URL of the logo to delete
 */
export const deleteRestaurantLogo = async (logoUrl) => {
  // Check if Supabase is configured
  if (!isSupabaseConfigured() || !supabase || !supabase.storage) {
    // For demo mode, just revoke the local object URL
    if (logoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(logoUrl);
    }
    return { success: true };
  }

  try {
    // Extract file path from URL
    // URL format: https://[project].supabase.co/storage/v1/object/public/restaurant-logos/logo-123.jpg
    const urlParts = logoUrl.split('/');
    const bucketIndex = urlParts.findIndex(part => part === 'restaurant-logos');
    
    if (bucketIndex === -1) {
      // If it's a local blob URL, just revoke it
      if (logoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(logoUrl);
        return { success: true };
      }
      throw new Error('Invalid logo URL format');
    }
    
    // Get the filename after 'restaurant-logos'
    const fileName = urlParts.slice(bucketIndex + 1).join('/');
    
    const { error } = await supabase.storage
      .from('restaurant-logos')
      .remove([fileName]);

    if (error) {
      console.error('Error deleting logo:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error in deleteRestaurantLogo:', error);
    throw error;
  }
};

